import { createContext, useContext, useEffect } from 'react';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';

import { frameTierFor } from 'components/Streaks/model/milestones';
import { FrameTier, StreakSnapshot } from 'components/Streaks/model/types';
import { requestSeatFrame } from 'components/Streaks/state/seatFrameLoader';
import { useStreakStore } from 'components/Streaks/state/streakStore';

/**
 * Set inside the /demo-table felt (undefined everywhere else): the frame the
 * POV seat wears for the demo's streak day, whatever the streaks flag or the
 * signed-in player's own snapshot says, and no other seat looks anything up,
 * so the demo still reaches no server.
 */
export const StreakFrameDemoContext = createContext<FrameTier | null | undefined>(undefined);

/** The player's own frame: none when they hid it or the streak ended. */
function ownFrameOf(snap: StreakSnapshot | null): FrameTier | null {
  if (!snap || !snap.showFrame || snap.status === 'ended') return null;
  return frameTierFor(snap.milestones, snap.currentStreak);
}

/**
 * The frame around the player's own avatar off the table (the account panel
 * and Account Profile; EN-11164), the same rule their seat uses. Reads only
 * their snapshot, which exists only for a signed-in player.
 */
export function useOwnStreakFrame(): FrameTier | null {
  const enabled = useFeatureFlag('streaks');
  const tier = useStreakStore((s) => ownFrameOf(s.snapshot));
  return enabled ? tier : null;
}

/**
 * The frame a seated player wears (EN-11164). The player's own seat reads
 * their snapshot, so a milestone or the "Show my streak frame" switch shows
 * at once. Every other seat asks `GET /streaks/frames` when it mounts (a
 * table opening or a new player sitting; batched in seatFrameLoader), then
 * follows `gotStreakFrame` pushes, which the server sends only when that
 * player's frame changes. Already null when the owner hid it or the streak ended.
 *
 * PERF: one selector over the streak store (not Redux) that returns a short
 * string, so a seat re-renders only when its tier actually changes.
 */
export function useSeatStreakFrame(guid: string | undefined): FrameTier | null {
  const demoFrame = useContext(StreakFrameDemoContext);
  const demo = demoFrame !== undefined;
  const enabled = useFeatureFlag('streaks') || demo;
  const guest = useGuest();
  const myGuid = useAppSelector((s) => s.user.playerGUID);
  const own = !!guid && guid === myGuid;
  const lookup = enabled && !demo && !guest && !!guid && !own;

  useEffect(() => {
    if (lookup && guid) requestSeatFrame(guid);
  }, [lookup, guid]);

  const tier = useStreakStore((s) => {
    if (!guid) return null;
    if (!own) return s.seatFrames[guid]?.frame ?? null;
    return demo ? demoFrame : ownFrameOf(s.snapshot);
  });
  return enabled ? tier : null;
}
